import type { ForecastPayload } from '../../types'

// Shared derived economics — the SINGLE source of truth for the P&L so the
// Overview headline, the Model tab, and the Finance forecast view agree to the
// penny. All money is integer PENCE. Formula transcribed exactly from the
// reference prototype's derive() (handoff EM-6).

export interface DerivedModel {
  att: number
  yieldPence: number
  costAdj: number
  ticketRevPence: number
  ancillaryPence: number
  grossRevPence: number
  baseCostsPence: number
  totalCostsPence: number
  eventProfitPence: number
  normalDaysPence: number
  incrementalPence: number
  displacedCount: number
}

// Uses the CURRENT scenario (sandbox) inputs by default. Pass an override to
// preview a different scenario without persisting.
export function deriveModel(fc: ForecastPayload, override?: Partial<{ att: number; yieldPence: number; costAdj: number }>): DerivedModel {
  const attFc = fc.attendanceFc ?? 0
  const att = override?.att ?? fc.scenario.att ?? attFc
  const yieldPence = override?.yieldPence ?? fc.scenario.yieldPence ?? fc.ticketYieldPence ?? 0
  const costAdj = override?.costAdj ?? fc.scenario.costAdj ?? 0

  // Mirrors api/src/lib/forecastLines.ts::deriveTotals to the penny. Ticket
  // revenue is att×yield; revenue lines that `scales` grow with attendance,
  // fixed lines don't; costs are the summed cost lines.
  const rev = fc.revenueStreams
  const scaleBase = rev.reduce((s, l) => s + (l.scales !== false ? (l.pence || 0) : 0), 0)
  const fixedRev = rev.reduce((s, l) => s + (l.scales === false ? (l.pence || 0) : 0), 0)
  const ticketRevPence = att * yieldPence
  const scaledAnc = attFc > 0 ? Math.round(scaleBase * (att / attFc)) : scaleBase
  const ancillaryPence = scaledAnc + fixedRev
  const grossRevPence = ticketRevPence + ancillaryPence

  const baseCostsPence = fc.costLines.reduce((s, l) => s + (l.pence || 0), 0) + (fc.contractFeesPence || 0)
  const totalCostsPence = Math.round(baseCostsPence * (1 + costAdj / 100))
  const eventProfitPence = grossRevPence - totalCostsPence

  const displacedCount = fc.proposed.dates.length
  const normalDaysPence = (fc.proposed.normalDayPence ?? 0) * displacedCount
  const incrementalPence = eventProfitPence - normalDaysPence

  return { att, yieldPence, costAdj, ticketRevPence, ancillaryPence, grossRevPence, baseCostsPence, totalCostsPence, eventProfitPence, normalDaysPence, incrementalPence, displacedCount }
}

export function hasProposedSlot(fc: ForecastPayload): boolean {
  return fc.proposed.dates.length > 0
}
